import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { fmt, fmtQty, unitLabel } from '../utils/format';
import Alert from '../components/Alert';
import InvoiceViewModal from '../components/InvoiceViewModal';

export default function Billing() {
  const [scanInput, setScanInput] = useState('');
  const [scanResults, setScanResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [items, setItems] = useState([]);
  const [customer, setCustomer] = useState({ name: '', phone: '', address: '' });
  const [customerId, setCustomerId] = useState(null);
  const [outstanding, setOutstanding] = useState(0); // prior dues of the selected existing customer
  const [customerStatus, setCustomerStatus] = useState(null); // 'existing' | 'new' | null
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showNameSugg, setShowNameSugg] = useState(false);
  const [showPhoneSugg, setShowPhoneSugg] = useState(false);
  const [fullyPaid, setFullyPaid] = useState(true);
  const [amountPaid, setAmountPaid] = useState('');
  const [collectModal, setCollectModal] = useState(false);
  const [collectAmount, setCollectAmount] = useState('');
  const [collectNote, setCollectNote] = useState('');
  const [collecting, setCollecting] = useState(false);
  const [alert, setAlert] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [createdInvoiceId, setCreatedInvoiceId] = useState(null);
  const [taxRate, setTaxRate] = useState(0);
  const scanFieldRef = useRef(null);

  useEffect(() => {
    scanFieldRef.current?.focus();
    api.get('/settings').then((res) => {
      setTaxRate(parseFloat(res.data.settings.tax_rate) || 0);
    }).catch(() => {});
  }, []);

  // Live product suggestions as the user types a name/SKU (debounced).
  useEffect(() => {
    const q = scanInput.trim();
    if (q.length < 1) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/products', { params: { q } });
        setSuggestions(res.data.products.slice(0, 8));
      } catch {
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [scanInput]);

  // Customer name suggestions (debounced).
  useEffect(() => {
    const q = customer.name.trim();
    if (q.length < 2) { setCustomerSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/customers', { params: { q } });
        setCustomerSuggestions(res.data.customers.slice(0, 6));
      } catch { setCustomerSuggestions([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [customer.name]);

  // Customer phone suggestions (debounced).
  useEffect(() => {
    const digits = customer.phone.replace(/\D/g, '');
    if (digits.length < 3) { setCustomerSuggestions([]); setCustomerStatus(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/customers', { params: { phone: digits } });
        setCustomerSuggestions(res.data.customers.slice(0, 6));
        // Exact match → mark as existing and pull in their outstanding dues
        const exact = res.data.customers.find((c) => c.phone_normalized === digits);
        if (exact) {
          setCustomerStatus('existing');
          setCustomerId(exact.id);
          setOutstanding(parseFloat(exact.outstanding_balance) || 0);
        } else {
          setCustomerStatus(digits.length >= 7 ? 'new' : null);
          setCustomerId(null);
          setOutstanding(0);
        }
      } catch { setCustomerSuggestions([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [customer.phone]);

  const subtotal = items.reduce((s, i) => s + (parseFloat(i.qty) || 0) * parseFloat(i.price), 0);
  const discount = items.reduce((s, i) => s + Math.max(parseFloat(i.mrp) - parseFloat(i.price), 0) * (parseFloat(i.qty) || 0), 0);
  const tax = parseFloat((subtotal * (taxRate / 100)).toFixed(2));
  const total = parseFloat((subtotal + tax).toFixed(2));

  // Prior dues only apply to a confirmed existing customer.
  const previousDue = customerStatus === 'existing' ? outstanding : 0;
  const totalPayable = parseFloat((total + previousDue).toFixed(2));
  // "Fully Paid" (default) means the whole payable is settled; otherwise the
  // cashier types how much actually came in and the rest is carried forward.
  const effectivePaid = fullyPaid ? totalPayable : Math.min(parseFloat(amountPaid) || 0, totalPayable);
  const newBalance = parseFloat(Math.max(totalPayable - effectivePaid, 0).toFixed(2));

  function addToInvoice(product) {
    if (parseFloat(product.qty) === 0) {
      setAlert({ type: 'danger', msg: `Out of stock: ${product.name}` });
      return;
    }
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.productId === product.id);
      if (idx >= 0) {
        const existing = prev[idx];
        if (existing.isLoose) return prev; // loose: user types the amount manually
        if (existing.qty >= parseFloat(product.qty)) {
          setAlert({ type: 'danger', msg: `Insufficient stock for ${product.name}` });
          return prev;
        }
        const updated = [...prev];
        updated[idx] = { ...existing, qty: existing.qty + 1 };
        return updated;
      }
      return [
        ...prev,
        {
          productId: product.id,
          name: product.name,
          sku: product.sku,
          mrp: product.mrp,
          price: product.price,
          basePack: product.base_pack,
          isLoose: !!product.is_loose,
          unit: product.unit || null,
          availableQty: parseFloat(product.qty),
          qty: product.is_loose ? 0 : 1,
        },
      ];
    });
  }

  async function doScan() {
    const q = scanInput.trim();
    if (!q) return;

    try {
      // Try barcode lookup first
      const byBarcodeRes = await api.get(`/products/barcode/${encodeURIComponent(q)}`).catch(() => null);
      if (byBarcodeRes?.data?.product) {
        addToInvoice(byBarcodeRes.data.product);
        setScanInput('');
        setScanResults([]);
        return;
      }

      // Fall back to name/sku search
      const searchRes = await api.get('/products', { params: { q } });
      const results = searchRes.data.products;
      if (results.length === 1) {
        addToInvoice(results[0]);
        setScanInput('');
        setScanResults([]);
      } else if (results.length > 1) {
        setScanResults(results);
      } else {
        setAlert({ type: 'danger', msg: `Product not found: ${q}` });
      }
    } catch (err) {
      setAlert({ type: 'danger', msg: 'Error looking up product.' });
    }
  }

  function pickScanResult(product) {
    addToInvoice(product);
    setScanInput('');
    setScanResults([]);
    setSuggestions([]);
    setShowSuggestions(false);
    scanFieldRef.current?.focus();
  }

  function removeItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  function changeQty(idx, delta) {
    setItems((prev) => {
      const item = prev[idx];
      const nq = item.qty + delta;
      if (nq < 1) {
        return prev.filter((_, i) => i !== idx);
      }
      if (nq > item.availableQty) {
        setAlert({ type: 'danger', msg: 'Insufficient stock.' });
        return prev;
      }
      const updated = [...prev];
      updated[idx] = { ...item, qty: nq };
      return updated;
    });
  }

  function setQty(idx, value) {
    setItems((prev) => {
      const item = prev[idx];
      const n = item.isLoose ? parseFloat(value) || 0 : parseInt(value, 10) || 1;
      if (n > item.availableQty) {
        setAlert({ type: 'danger', msg: 'Insufficient stock.' });
        return prev;
      }
      const updated = [...prev];
      updated[idx] = { ...item, qty: item.isLoose ? Math.max(0, n) : Math.max(1, n) };
      return updated;
    });
  }

  function pickCustomer(c) {
    setCustomer({ name: c.name || '', phone: c.phone || '', address: c.address || '' });
    setCustomerId(c.id);
    setOutstanding(parseFloat(c.outstanding_balance) || 0);
    setCustomerStatus('existing');
    setCustomerSuggestions([]);
    setShowNameSugg(false);
    setShowPhoneSugg(false);
  }

  function clearBilling() {
    setItems([]);
    setCustomer({ name: '', phone: '', address: '' });
    setCustomerId(null);
    setOutstanding(0);
    setCustomerStatus(null);
    setCustomerSuggestions([]);
    setFullyPaid(true);
    setAmountPaid('');
    setScanInput('');
    setScanResults([]);
  }

  async function generateInvoice() {
    if (items.length === 0) return;
    const zeroLoose = items.find((i) => i.isLoose && !(parseFloat(i.qty) > 0));
    if (zeroLoose) {
      setAlert({ type: 'danger', msg: `Enter a quantity for "${zeroLoose.name}".` });
      return;
    }
    // Leaving a balance requires an identified customer (need a phone to owe against).
    if (newBalance > 0 && !customer.phone.trim()) {
      setAlert({ type: 'danger', msg: 'Enter a customer phone number to leave a balance unpaid (credit).' });
      return;
    }
    setGenerating(true);
    setAlert(null);
    try {
      const res = await api.post('/invoices', {
        customerName: customer.name || null,
        customerPhone: customer.phone || null,
        customerAddress: customer.address || null,
        items: items.map((i) => ({ productId: i.productId, qty: i.qty })),
        amountPaid: effectivePaid,
      });
      const invoice = res.data.invoice;
      clearBilling();
      setCreatedInvoiceId(invoice.id);
    } catch (err) {
      setAlert({ type: 'danger', msg: err?.response?.data?.error || 'Failed to generate invoice.' });
    } finally {
      setGenerating(false);
    }
  }

  function openCollect() {
    setCollectAmount(String(outstanding));
    setCollectNote('');
    setCollectModal(true);
  }

  async function submitCollect() {
    const amt = parseFloat(collectAmount);
    if (isNaN(amt) || amt <= 0) {
      setAlert({ type: 'danger', msg: 'Enter a valid payment amount.' });
      return;
    }
    setCollecting(true);
    try {
      const res = await api.post(`/customers/${customerId}/payments`, { amount: amt, note: collectNote || null });
      setOutstanding(parseFloat(res.data.customer.outstanding_balance) || 0);
      setCollectModal(false);
      setAlert({ type: 'success', msg: `Payment of ${fmt(amt)} recorded.` });
    } catch (err) {
      setAlert({ type: 'danger', msg: err?.response?.data?.error || 'Failed to record payment.' });
    } finally {
      setCollecting(false);
    }
  }

  return (
    <Layout title="New Invoice">
      <Alert type={alert?.type} message={alert?.msg} onClose={() => setAlert(null)} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.25rem' }}>
        <div>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div className="card-header">
              <div className="card-title">Scan / Search Product</div>
            </div>
            <div className="scan-input">
              <input
                ref={scanFieldRef}
                placeholder="Scan barcode or type product name, press Enter…"
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') doScan();
                }}
              />
              <button className="btn btn-primary" onClick={doScan}>
                <i className="ti ti-barcode" aria-hidden="true"></i>Add
              </button>
            </div>
            <div className="barcode-hint">
              Press Enter after scanning with a USB scanner or typing a product name
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <div style={{ marginTop: '.75rem', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ padding: '.45rem .75rem', background: 'var(--green-light)', fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>
                  Suggestions:
                </div>
                {suggestions.map((p) => (
                  <div
                    key={p.id}
                    onMouseDown={() => pickScanResult(p)}
                    style={{
                      padding: '.5rem .75rem',
                      cursor: 'pointer',
                      fontSize: 12,
                      borderBottom: '1px solid #f5f5f5',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '.5rem',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                    onMouseOut={(e) => (e.currentTarget.style.background = '')}
                  >
                    <span>
                      {p.name} &nbsp;<span style={{ color: '#9e9e9e' }}>{p.sku}</span>
                    </span>
                    <span style={{ color: parseFloat(p.qty) === 0 ? 'var(--danger, #e53935)' : '#9e9e9e', whiteSpace: 'nowrap' }}>
                      {parseFloat(p.qty) === 0 ? 'Out of stock' : `${fmt(p.mrp)} MRP · ${fmtQty(p.qty, p.unit)} in stock`}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {scanResults.length > 1 && (
              <div style={{ marginTop: '.75rem', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ padding: '.45rem .75rem', background: 'var(--green-light)', fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>
                  Select product:
                </div>
                {scanResults.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => pickScanResult(p)}
                    style={{ padding: '.5rem .75rem', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f5f5f5' }}
                    onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                    onMouseOut={(e) => (e.currentTarget.style.background = '')}
                  >
                    {p.name} &nbsp;<span style={{ color: '#9e9e9e' }}>{p.sku}</span> — {fmt(p.mrp)} MRP
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Invoice Items ({items.length})</div>
            </div>
            {items.length === 0 ? (
              <div className="empty-state">
                <i className="ti ti-shopping-cart" aria-hidden="true"></i>
                No items added yet
                <br />
                <small>Scan or search a product above</small>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item</th>
                    <th>MRP</th>
                    <th>Base Pack</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td style={{ fontWeight: 600, fontSize: 11 }}>{it.name}</td>
                      <td>{fmt(it.mrp)}</td>
                      <td>{it.isLoose ? '—' : it.basePack}</td>
                      <td>
                        {it.isLoose ? (
                          <div className="qty-control">
                            <div className="qty-loose">
                              <input
                                type="number"
                                value={it.qty || ''}
                                min="0"
                                step="0.001"
                                placeholder="0.000"
                                onChange={(e) => setQty(idx, e.target.value)}
                              />
                              <span className="qty-loose-unit">{unitLabel(it.unit)}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="qty-control">
                            <button onClick={() => changeQty(idx, -1)}>-</button>
                            <input
                              type="number"
                              value={it.qty}
                              min="1"
                              step="1"
                              onChange={(e) => setQty(idx, e.target.value.replace(/\.\d*/, ''))}
                            />
                            <button onClick={() => changeQty(idx, 1)}>+</button>
                          </div>
                        )}
                      </td>
                      <td>{fmt(it.price)}</td>
                      <td>
                        <strong>{fmt((parseFloat(it.qty) || 0) * parseFloat(it.price))}</strong>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-danger" onClick={() => removeItem(idx)}>
                          <i className="ti ti-trash" aria-hidden="true"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div className="card-header">
              <div className="card-title">Bill To</div>
              {customerStatus === 'existing' && (
                <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: 'var(--green)', padding: '2px 8px', borderRadius: 12 }}>
                  Existing Customer
                </span>
              )}
              {customerStatus === 'new' && (
                <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: '#1976d2', padding: '2px 8px', borderRadius: 12 }}>
                  New Customer
                </span>
              )}
              {customerStatus === 'existing' && outstanding > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--danger, #e53935)', padding: '2px 8px', borderRadius: 12 }}>
                  Outstanding: {fmt(outstanding)}
                </span>
              )}
            </div>
            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label">Customer / Store Name</label>
              <input
                placeholder="e.g. SAI V STORE KLPTN"
                value={customer.name}
                onChange={(e) => { setCustomerStatus(null); setCustomer((c) => ({ ...c, name: e.target.value })); }}
                onFocus={() => setShowNameSugg(true)}
                onBlur={() => setTimeout(() => setShowNameSugg(false), 150)}
              />
              {showNameSugg && customerSuggestions.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, border: '1px solid var(--border)', borderRadius: 6, background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,.1)', overflow: 'hidden' }}>
                  {customerSuggestions.map((c) => (
                    <div
                      key={c.id}
                      onMouseDown={() => pickCustomer(c)}
                      style={{ padding: '.5rem .75rem', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f5f5f5' }}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                      onMouseOut={(e) => (e.currentTarget.style.background = '')}
                    >
                      <div style={{ fontWeight: 600 }}>{c.name || '—'}</div>
                      <div style={{ color: '#9e9e9e', fontSize: 11 }}>{c.phone}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label">Phone</label>
              <input
                placeholder="Phone number"
                value={customer.phone}
                onChange={(e) => { setCustomerStatus(null); setCustomer((c) => ({ ...c, phone: e.target.value })); }}
                onFocus={() => setShowPhoneSugg(true)}
                onBlur={() => setTimeout(() => setShowPhoneSugg(false), 150)}
              />
              {showPhoneSugg && customerSuggestions.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, border: '1px solid var(--border)', borderRadius: 6, background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,.1)', overflow: 'hidden' }}>
                  {customerSuggestions.map((c) => (
                    <div
                      key={c.id}
                      onMouseDown={() => pickCustomer(c)}
                      style={{ padding: '.5rem .75rem', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f5f5f5' }}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                      onMouseOut={(e) => (e.currentTarget.style.background = '')}
                    >
                      <div style={{ fontWeight: 600 }}>{c.phone}</div>
                      <div style={{ color: '#9e9e9e', fontSize: 11 }}>{c.name || '—'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <textarea
                rows={2}
                placeholder="Address"
                value={customer.address}
                onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))}
              />
            </div>
            {customerStatus === 'existing' && customerId && outstanding > 0 && (
              <button
                className="btn btn-sm"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={openCollect}
              >
                <i className="ti ti-cash" aria-hidden="true"></i>
                Collect Payment ({fmt(outstanding)} due)
              </button>
            )}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem 0', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
              <span>Sub Total</span>
              <span>{fmt(subtotal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem 0', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
              <span>Discount (MRP savings)</span>
              <span style={{ color: 'var(--green)' }}>{fmt(discount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem 0', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
              <span>Tax (GST {taxRate}%)</span>
              <span>{fmt(tax)}</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '.6rem .5rem',
                marginTop: 6,
                background: 'var(--green)',
                color: '#fff',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              <span>Grand Total</span>
              <span>{fmt(total)}</span>
            </div>

            {previousDue > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem 0', fontSize: 12, color: 'var(--danger, #e53935)', borderBottom: '1px solid #f0f0f0', marginTop: 6 }}>
                  <span>Previous Dues</span>
                  <span>{fmt(previousDue)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem 0', fontSize: 13, fontWeight: 700, borderBottom: '1px solid #f0f0f0' }}>
                  <span>Total Payable</span>
                  <span>{fmt(totalPayable)}</span>
                </div>
              </>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={fullyPaid}
                onChange={(e) => setFullyPaid(e.target.checked)}
              />
              <strong>Fully Paid</strong>
            </label>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '.5rem 0', fontSize: 12 }}>
              <span>Amount Paid</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={fullyPaid ? totalPayable : amountPaid}
                disabled={fullyPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                placeholder="0"
                style={{ width: 120, textAlign: 'right', background: fullyPaid ? '#f5f5f5' : '#fff' }}
              />
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '.45rem .5rem',
                background: newBalance > 0 ? 'var(--danger, #e53935)' : 'var(--green)',
                color: '#fff',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              <span>{newBalance > 0 ? 'New Balance (carried)' : 'Balance'}</span>
              <span>{fmt(newBalance)}</span>
            </div>

            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: '.85rem', padding: '.55rem' }}
              onClick={generateInvoice}
              disabled={items.length === 0 || generating}
            >
              <i className="ti ti-receipt" aria-hidden="true"></i>
              {generating ? 'Generating...' : 'Generate Invoice'}
            </button>
            <button
              className="btn"
              style={{ width: '100%', justifyContent: 'center', marginTop: '.5rem' }}
              onClick={clearBilling}
            >
              <i className="ti ti-trash" aria-hidden="true"></i>Clear
            </button>
          </div>
        </div>
      </div>

      {createdInvoiceId && (
        <InvoiceViewModal
          invoiceId={createdInvoiceId}
          onClose={() => setCreatedInvoiceId(null)}
        />
      )}

      {collectModal && (
        <div
          className="modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setCollectModal(false); }}
        >
          <div className="modal" style={{ width: 380 }}>
            <div className="modal-header">
              <div className="modal-title">Collect Payment</div>
              <button className="btn btn-sm" onClick={() => setCollectModal(false)}>
                <i className="ti ti-x" aria-hidden="true"></i>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, marginBottom: '.75rem' }}>
                {customer.name || 'Customer'} owes <strong style={{ color: 'var(--danger, #e53935)' }}>{fmt(outstanding)}</strong>
              </div>
              <div className="form-group">
                <label className="form-label">Amount Received</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={collectAmount}
                  onChange={(e) => setCollectAmount(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label className="form-label">Note (optional)</label>
                <input
                  type="text"
                  value={collectNote}
                  onChange={(e) => setCollectNote(e.target.value)}
                  placeholder="e.g. cash, UPI ref…"
                />
              </div>
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={submitCollect}
                disabled={collecting}
              >
                <i className="ti ti-cash" aria-hidden="true"></i>
                {collecting ? 'Recording…' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
